import React from 'react';
import * as ContextMenu from '@radix-ui/react-context-menu';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Check, ChevronRight } from 'lucide-react';
import { useActionConfirm } from '../hooks/useActionConfirm';
import './PlaytestCardContextMenu.css';

/**
 * Kontextmenüs des Bretts am Rechner. Sie rendern Einträge aus `utils/cardActions.js`
 * (Untermenü = `children`); dieselben Listen zeigt am Handy `CardActionSheet` bzw. `ActionSheet`.
 *
 * `ActionContextMenu` — Radix-Kontextmenü um ein Element, das Rechtsklicks selbst empfangen darf:
 *   <ZoneTile … contextMenu={(tile) => (
 *     <ActionContextMenu actions={deckActions(ctx)}>{tile}</ActionContextMenu>
 *   )} />
 *   Das Kind muss Ref und Handler an sein DOM-Element weiterreichen (`Trigger asChild`). Ohne
 *   Einträge ist der Auslöser gesperrt, ein Rechtsklick öffnet nichts; die Hülle bleibt stehen,
 *   damit das Kind beim Wechsel nicht neu eingehängt wird (Ablageziele einer Kachel).
 *
 * `CardContextMenu` — Menü an einem Bildschirmpunkt, für Karten, die ihren Rechtsklick selbst
 * abfangen und nur `onOpenMenu(…, { x, y })` melden (BattlefieldCard, HandFan, CardListViewer):
 *   const [menu, setMenu] = useState(null);   // { point, actions }
 *   onCardOpenMenu={(id, point) => setMenu({ point, actions: battlefieldCardActions(card, ctx) })}
 *   <CardContextMenu point={menu?.point} actions={menu?.actions} onClose={() => setMenu(null)} />
 *   Offen, solange `point` gesetzt ist und es Einträge gibt; Auswahl, Escape oder ein Klick
 *   daneben rufen `onClose`. Nicht modal: ein Klick daneben schließt und erreicht zugleich sein
 *   Ziel, ein Rechtsklick auf die nächste Karte öffnet deren Menü direkt.
 *   Außerhalb transformierter Vorfahren rendern (Kamera-Ebene: `position: fixed` des Ankers
 *   bezöge sich sonst auf sie); aus dem Zonen-Viewer geöffnet im React-Baum des Viewers, damit
 *   Radix das Menü als Ebene über dem Dialog erkennt.
 *
 * Einträge zeigen `shortcut` als Tastenhinweis, `badge` als Zahl, `checked` als Häkchen;
 * `danger` färbt, `separatorBefore` setzt eine Trennlinie davor. Einträge mit `confirm` fragen
 * nach dem Schließen des Menüs per `ConfirmDialog` nach (`useActionConfirm`), wie die Sheets.
 */
export function ActionContextMenu({ children, actions = [] }) {
  const { select, dialog } = useActionConfirm();
  return (
    <>
      <ContextMenu.Root>
        <ContextMenu.Trigger asChild disabled={actions.length === 0}>{children}</ContextMenu.Trigger>
        <ContextMenu.Portal>
          <ContextMenu.Content className="gcm-content" collisionPadding={8}>
            <MenuEntries actions={actions} menu={ContextMenu} onSelect={select} />
          </ContextMenu.Content>
        </ContextMenu.Portal>
      </ContextMenu.Root>
      {dialog}
    </>
  );
}

export function CardContextMenu({ point, actions = [], onClose }) {
  const { select, dialog } = useActionConfirm();
  const open = !!point && actions.length > 0;
  return (
    <>
      <DropdownMenu.Root open={open} onOpenChange={(next) => { if (!next) onClose?.(); }} modal={false}>
        <DropdownMenu.Trigger asChild>
          <span
            className="gcm-anchor"
            style={{ left: point?.x ?? 0, top: point?.y ?? 0 }}
            aria-hidden
            tabIndex={-1}
          />
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            className="gcm-content"
            aria-label="Kartenaktionen"
            // Radix verweist sonst per aria-labelledby auf den namenlosen Anker und überdeckt aria-label.
            aria-labelledby={undefined}
            side="bottom"
            align="start"
            sideOffset={2}
            collisionPadding={8}
            // Der Anker ist ein unsichtbarer Punkt; Fokus dorthin zurückzulegen hilft niemandem.
            onCloseAutoFocus={(event) => event.preventDefault()}
          >
            <MenuEntries actions={actions} menu={DropdownMenu} onSelect={select} />
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
      {dialog}
    </>
  );
}

/**
 * Einträge für Radix-Kontext- oder Dropdown-Menü; beide haben dieselben Bausteine, `menu` ist
 * der jeweilige Namensraum.
 */
function MenuEntries({ actions, menu: M, onSelect }) {
  return actions.map((entry) => {
    const Icon = entry.icon;
    return (
      <React.Fragment key={entry.id}>
        {entry.separatorBefore && <M.Separator className="gcm-separator" />}
        {entry.children ? (
          <M.Sub>
            <M.SubTrigger className="gcm-item gcm-subtrigger" disabled={entry.disabled}>
              {Icon && <Icon size={14} aria-hidden />}
              <span>{entry.label}</span>
              <ChevronRight size={14} className="gcm-chevron" aria-hidden />
            </M.SubTrigger>
            <M.Portal>
              <M.SubContent className="gcm-content gcm-content--sub" sideOffset={4} collisionPadding={8}>
                <MenuEntries actions={entry.children} menu={M} onSelect={onSelect} />
              </M.SubContent>
            </M.Portal>
          </M.Sub>
        ) : (
          <M.Item
            className={`gcm-item${entry.danger ? ' gcm-item--danger' : ''}${entry.checked ? ' gcm-item--checked' : ''}`}
            disabled={entry.disabled}
            onSelect={() => onSelect(entry)}
          >
            {Icon && <Icon size={14} aria-hidden />}
            <span>{entry.label}</span>
            {entry.badge != null && <span className="gcm-badge">{entry.badge}</span>}
            {entry.checked && <Check size={14} className="gcm-check" aria-hidden />}
            {entry.shortcut && <kbd className="gcm-shortcut">{entry.shortcut}</kbd>}
          </M.Item>
        )}
      </React.Fragment>
    );
  });
}
